import { EntityFactory } from './level/EntityFactory';
import { ObstacleFactory } from './level/ObstacleFactory';
import { ChargeStage, Level, LevelStage, LootStage, RampStage, Stage, StationDetails, LevelName } from './level/levels/level';
import { LevelUtil } from './level/levels/LevelUtil';
import { Theme, LaneItemDetails } from './level/themes/theme';
import Obstacle, { ObstacleNameValues } from './level/entities/obstacle/Obstacle';
import { MathExtras } from '../core/utils/MathExtras';
import { LaneEntity } from './level/entities/LaneEntity';
import { Vehicle } from './vehicle/Vehicle';
import { EntityType, EntityTypeValues } from './level/entities/Entities';
import MoneyBills from './level/entities/MoneyBills';
import MoneyBag from './level/entities/MoneyBag';
import { Station, StationState } from './level/Station';
import ChargingStation from './level/entities/ChargingStation';
import { ActionRunner } from '../core/modules/ActionRunner/ActionRunner';
import { ActionBlock } from '../core/modules/ActionRunner/ActionBlock';
import { AIBoss } from './vehicle/AIBoss';
import { VehicleManager } from './vehicle/VehicleManager';
import { RunSettingsHandler } from './RunSettingsHandler';
import { CameraBall } from './helpers/CameraBall';
import MoneySuperBag from './level/entities/MoneySuperBag';
import { ChargeStrip, ChargeStripSkins, ChargeStripSkinsValues } from './level/entities/ChargeStrip';
import { RampGuide } from './level/RampGuide';
import EntityMoney from './level/entities/EntityMoney';
import MoneyBillsBalloon from './level/entities/MoneyBillsBalloon';
import MoneyBagBalloon from './level/entities/MoneyBagBalloon';
import { VehicleVillain } from './vehicle/VehicleVillain';
import { LevelFeature } from './level/LevelFeature';
import { RingFeature } from './level/entities/RingFeature';
import { RampFeature } from './level/entities/RampFeature';
import MoneySuperBagBalloon from './level/entities/MoneySuperBagBalloon';
import { RampSkinsValues } from './level/entities/Ramp';

export interface LaneWeight {
  laneNumber: number;
  distance: number;
  weight: number;
}

export interface ItemConfig {
  className: string;
  maxSpawned: number;
  timeBetweenSpawns: number;
}

export interface ItemTracking {
  className: string;
  currentSpawned: number;
  lastSpawn: number;
}

export interface LaneZone {
  startX: number;
  endX: number;
}

export interface LaneItem {
  // classification: ObjectClassificationValues;
  distance: number;
}

export const BodyTypes = {
  Vehicle: 'vehicle',
  VehicleSensor: 'vehicleSensor',
  TaxiRank: 'taxiRank',
  TaxiDropOff: 'taxiDropOff',
  Boost: 'boost',
  Obstacle: 'obstacle',
  ChargingStation: 'chargingStation',
  Weapon: 'weapon',
  Ramp: 'ramp',
  RampGuide: 'rampGuide'
} as const;

export class LevelManager {
  protected scene: Phaser.Scene;
  protected currentLevel: Level;
  protected currentTheme: Theme;
  protected obstacleFactory: ObstacleFactory;
  protected entityFactory: EntityFactory;
  protected vehicleManager: VehicleManager | undefined;
  protected camera: Phaser.Cameras.Scene2D.Camera | undefined;
  protected cameraBall: CameraBall | undefined;
  protected floorLayer: Phaser.GameObjects.Layer | undefined;
  protected vehicleLayer: Phaser.GameObjects.Layer | undefined;
  protected foregroundPavementLayer: Phaser.GameObjects.Layer | undefined;

  protected obstacleList: typeof Obstacle[];
  protected itemConfiguration: Map<string, ItemConfig>;

  protected stations: Station[];
  protected chargingStations: ChargingStation[];
  protected itemZones: Map<number, LaneZone[]>;
  protected spawnedItems: Map<number, LaneEntity[]>;
  protected spawnedObstacles: Map<number, Obstacle[]>;
  protected lastObstacleSpawnedPerLane: Map<number, number>;
  protected lastBoostPadSpawnedPerLane: Map<number, number>;
  protected lastSpawnedDropOff: number;
  protected itemTracking: Map<string, ItemTracking>;
  protected previousSpawnEdgeX: number;
  protected endPointX: number;
  protected onPowerChargeChange:any;

  protected blockedZones: Map<Phaser.GameObjects.GameObject, Phaser.Geom.Rectangle>;

  protected stageRunner: ActionRunner;

  constructor(scene: Phaser.Scene, level: Level, theme: Theme) {
    this.scene = scene;
    this.currentLevel = level;
    this.currentTheme = theme;

    this.obstacleFactory = new ObstacleFactory(this.scene);
    this.entityFactory = new EntityFactory(this.scene);

    this.obstacleList = [];
    this.itemConfiguration = new Map();

    this.stations = [];
    this.chargingStations = [];

    this.itemZones = new Map();
    this.spawnedItems = new Map();
    this.spawnedObstacles = new Map();
    this.lastObstacleSpawnedPerLane = new Map();
    this.lastBoostPadSpawnedPerLane = new Map();
    this.lastSpawnedDropOff = 0;
    this.itemTracking = new Map();
    this.previousSpawnEdgeX = 0;
    this.endPointX = 0;

    this.blockedZones = new Map();

    this.stageRunner = new ActionRunner();
  }

  get CurrentLevel(): Level {
    return this.currentLevel;
  }

  // get ItemDetails(): LaneItemDetails {
  //   return this.currentLevel.laneItemDetails;
  // }

  get CurrentTheme(): Theme {
    return this.currentTheme;
  }

  get CurrentLevelStage(): Stage {
    return this.currentLevel.stages[this.stageRunner.CurrentActionIndex];
  }

  get IsLevelFinished(): boolean {
    return this.stageRunner.IsRunning;
  }

  get LevelEndPointX(): number {
    let cameraSafeZone = 500;
    return this.endPointX + cameraSafeZone;
  }

  get BlockedZones(): Map<Phaser.GameObjects.GameObject, Phaser.Geom.Rectangle> {
    return this.blockedZones;
  }

  public init(
    vehicleManager: VehicleManager,
    camera: Phaser.Cameras.Scene2D.Camera,
    cameraBall: CameraBall,
    floorLayer: Phaser.GameObjects.Layer,
    vehicleLayer: Phaser.GameObjects.Layer,
    foregroundLayer: Phaser.GameObjects.Layer
  ) {
    this.vehicleManager = vehicleManager;
    this.camera = camera;
    this.cameraBall = cameraBall;
    this.floorLayer = floorLayer;
    this.vehicleLayer = vehicleLayer;
    this.foregroundPavementLayer = foregroundLayer;

    // this.obstacleFactory.init();
    // this.entityFactory.init();

    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  public changeLevel(level: Level, theme: Theme) {
    this.currentLevel = level;
    this.currentTheme = theme;
  }

  public setupLevel(startX: number, startY: number) {
    this.clearLevel();

    let levelStages: ActionBlock[] = [];
    for (const stage of this.currentLevel.stages) {
      switch (stage.stageType) {
        case LevelStage.LootDropSection:
          levelStages.push(this.lootDropStage(stage as LootStage));
          break;

        case LevelStage.RampSection:
          levelStages.push(this.rampStage(stage as RampStage));
          break;

        case LevelStage.ChargeUpSection:
          levelStages.push(this.chargeUpStage(stage as ChargeStage));
          break;

        default:
          break;
      }
    }
    this.stageRunner.setActions(levelStages);
  }

  public startLevel() {
    this.stageRunner.start();
  }

  public updateLevel(time: number, delta: number) {
    this.stageRunner.update(time, delta);
  }

  public clearLevel() {
    this.obstacleList = [];
    this.itemConfiguration.clear();

    this.spawnedItems.clear();
    this.spawnedObstacles.clear();

    for (let i = 0; i < this.currentTheme.roadDetails.laneCount; i++) {
      this.itemZones.set(i, []);
      this.spawnedItems.set(i, []);
      this.lastObstacleSpawnedPerLane.set(i, 0);
      this.lastBoostPadSpawnedPerLane.set(i, 0);
    }
  }

  public setEndpoint(newEndpoint: number) {
    this.endPointX = newEndpoint;
  }

  public registerBlockZone(objectKey: Phaser.GameObjects.GameObject, zone: Phaser.Geom.Rectangle) {
    this.blockedZones.set(objectKey, zone);
  }

  public deregisterBlockZone(objectKey: Phaser.GameObjects.GameObject) {
    this.blockedZones.delete(objectKey);
  }

  public spawnChargingStation(station: Station, skin: ChargeStripSkinsValues) {
    // Spawn Charging Station.
    if (this.chargingStations.length > 0) {
      // let chargingStation = this.chargingStations[0];
      // chargingStation.x = station.PickupPoint;
      // chargingStation.y = this.CurrentTheme.isSky ? 0 : LevelUtil.RoadMiddle;
      // chargingStation.setStation(station);
    } else {
      let y = this.CurrentTheme.isSky ? 0 : LevelUtil.RoadMiddle;
      let chargingStation = new ChargeStrip(this.scene, station.PickupPoint, y, skin);
      chargingStation.init();
      chargingStation.setStation(station);
      this.floorLayer?.add(chargingStation as unknown as Phaser.GameObjects.GameObject);
      // this.chargingStations.push(chargingStation);
    }
  }

  public spawnBehindVehicle(vehicle: VehicleVillain, entityType: EntityTypeValues, spawnOffsetX: number, spawnOffsetY: number) {
    // let spawnOffsetY = -20;

    if (entityType == EntityType.MoneyBills) {
      let money: EntityMoney;
      if (this.CurrentTheme.isSky) money = new MoneyBillsBalloon(this.scene, vehicle.x, vehicle.y);
      else money = new MoneyBills(this.scene, vehicle.x, vehicle.y);

      money.setDepth(-1);
      this.vehicleLayer?.add(money);
      money.dropMoney(vehicle);
    } else if (entityType == EntityType.MoneyBag) {
      let money: EntityMoney;
      if (this.CurrentTheme.isSky) money = new MoneyBagBalloon(this.scene, vehicle.x, vehicle.y);
      else money = new MoneyBag(this.scene, vehicle.x, vehicle.y );

      money.setDepth(-1);
      this.vehicleLayer?.add(money);
      money.dropMoney(vehicle);
    } else if (entityType == EntityType.SuperMoneyBag) {
      if (this.CurrentTheme.isSky) this.spawnSuperBagBalloon(vehicle, vehicle.y + spawnOffsetY);
      else this.spawnSuperBag(vehicle, vehicle.y + spawnOffsetY);
    }
  }

  public despawnEntitiesBehindOrUsed(time: number, delta: number, leftViewBoundsX: number) {
    const safeDespawnDistance: number = 500;

    for (const [laneNumber, laneEntities] of this.spawnedItems) {
      if (laneEntities.length <= 0) continue;

      for (let i = laneEntities.length - 1; i >= 0; i--) {
        const entity = laneEntities[i];

        if (entity.HasBeenHit || entity.x <= leftViewBoundsX - safeDespawnDistance) {
          laneEntities.splice(i, 1);
          // this.untrackItem(entity, laneNumber);
          entity.recycle();
        }
      }
    }
  }

  // Tear down.
  public endLevel() {}

  protected lootDropStage(lootStage: LootStage): ActionBlock {
    return {
      actionName: LevelStage.LootDropSection,
      onActivate: (runner, callback) => {
        this.scene.events.once(AIBoss.events.StoppedSpawningLoot, () => {
          // this.vehicleManager.
          callback();
        });

        // Configure villain to drop loot.
        this.vehicleManager?.dropLoot(lootStage);

        // Bind to event when all loot dropped?
      }
      // onUpdate: (runner, time, delta, callback) => {
      //   // Wait for all loot to be dropped.

      //   return false;
      // },
      // OnDeactivate: (runner, callback) => {
      //   // Unbind event.

      //   this.scene.events.off(AIBoss.events.StoppedSpawningLoot);
      // }
    };
  }

  protected rampStage(rampStage: RampStage): ActionBlock {
    let safeDistance = 1000;
    let jumpDistance = 500;
    let rampOffsetStartY = 40;
    let rampOffsetEndY = 20;

    return {
      actionName: LevelStage.RampSection,
      onActivate: (runner, callback) => {
        if (!this.camera) return;

        // Spawn ramp.
        // Tell villain to go over ramp.
        // Spawn ramp loot.

        // let rampY = 70;
        // let rampY = this.CurrentTheme.roadDetails.roadStartY + rampOffsetStartY;
        let rampY = this.CurrentTheme.roadDetails.roadStartY - 92 + 180;
        // let rampY = this.CurrentTheme.roadDetails.roadEndY - 112;
        // let rampY = Phaser.Math.Between(this.CurrentTheme.roadDetails.roadStartY + rampOffset, this.CurrentTheme.roadDetails.roadEndY + rampOffset);

        let ramp = this.spawnFeature((this.camera.worldView.right ?? 0) + safeDistance, rampY, rampStage.rampSkin);
        this.vehicleManager?.takeRamp(rampStage, ramp);

        this.scene.time.delayedCall(5000, () => {
          callback();
        });
      }
      // onUpdate: (runner, time, delta, callback) => {
      //   // Wait for hero to be X distance past ramp.

      //   return false;
      // }
    };
  }

  protected chargeUpStage(chargeStage: ChargeStage): ActionBlock {
    let safeDistance = 250;

    this.onPowerChargeChange = () => {};

    let wrap = (callback: (params?: any) => void) => {
      this.onPowerChargeChange = () => {
        if (!this.cameraBall) return;

        // If we have all charges then we want to catch up as part of the capture sequence.
        if (RunSettingsHandler.hasCollectedAllCharges(this.scene)) {
          callback();
        } else {
          this.vehicleManager?.HeroVehicle?.panBySpeedToRelativePointX(this.cameraBall.x + 850, -350);
          this.cameraBall.panXToRelativePoint(850);

          this.scene.time.delayedCall(3500, () => {
            if (!this.cameraBall) return;

            this.vehicleManager?.VillainVehicle?.panByTimeToXRelativePointX(this.cameraBall.x, 350, 500, Phaser.Math.Easing.Linear);
            this.vehicleManager?.HeroVehicle?.panByTimeToXRelativePointX(this.cameraBall.x, -350, 500, Phaser.Math.Easing.Linear);
            // this.vehicleManager?.HeroVehicle?.panBySpeedToRelativePointX(this.cameraBall.x + 850, -350);
            // this.cameraBall.panXToRelativePoint(850);
            callback();
          });
        }
      };
    };

    return {
      actionName: LevelStage.ChargeUpSection,
      onActivate: (runner, callback) => {
        if (!this.cameraBall) return;

        wrap(callback);
        // Bind on event charges changing?
        this.scene.registry.events.once(RunSettingsHandler.events.ChangeChargesCollected, this.onPowerChargeChange, this);

        // this.vehicleManager?.VillainVehicle?.panBySpeedToRelativePointX(this.cameraBall.x, 1200);
        this.vehicleManager?.VillainVehicle?.panByTimeToXRelativePointX(this.cameraBall.x, 1200, 2000, Phaser.Math.Easing.Linear);

        this.scene.time.delayedCall(1500, () => {
          if (!this.camera) return;

          // Spawn charge lane.
          let station = new Station(this.camera.worldView.right + safeDistance, chargeStage.chargeAmount ?? 1);
          this.spawnChargingStation(station, chargeStage.skin ?? ChargeStripSkins.Normal);
          station.spawned();
          // callback();
        });

        // this.vehicleManager.HeroVehicle?.panXToRelativePoint(850);

        // this.scene.time.delayedCall(1500, () => {
        //   callback();
        // });
      }
      // OnDeactivate: (runner, callback) => {
      //   // Unbind event.
      //   this.scene.registry.events.off(RunSettingsHandler.events.ChangeChargesCollected, onPowerChargeChange, this);
      // }
    };
  }

  protected spawnFeature(x: number, y: number, skin: RampSkinsValues): LevelFeature {
    if (this.CurrentLevel.id == LevelName.Level4 && this.vehicleLayer) {
      let ring = new RingFeature(this.scene, this.vehicleLayer, x, y);
      ring.init();
      this.registerBlockZone(ring, ring.getBlockZone());
      // this.vehicleLayer?.add(ring);

      let rampGuide = new RampGuide(this.scene, ring);

      return ring;
    } else {
      let ramp = new RampFeature(this.scene, x, y, "game", skin);
      ramp.init();
      this.registerBlockZone(ramp, ramp.getBlockZone());
      this.vehicleLayer?.add(ramp);

      let rampGuide = new RampGuide(this.scene, ramp);

      return ramp;
    }
  }

  protected spawnSuperBag(vehicle: Vehicle, offsetY: number) {
    let money = new MoneySuperBag(this.scene, vehicle.x, offsetY);
    money.setDepth(vehicle.y);
    this.vehicleLayer?.add(money);
  }

  protected spawnSuperBagBalloon(vehicle: Vehicle, offsetY: number) {
    let money = new MoneySuperBagBalloon(this.scene, vehicle.x, offsetY);
    money.setDepth(vehicle.y);
    this.vehicleLayer?.add(money);
  }

  protected shutdown() {
    this.spawnedItems.clear();
    this.spawnedObstacles.clear();

    this.itemTracking.clear();
    this.stageRunner.clear();

    this.scene.registry.events.off(RunSettingsHandler.events.ChangeChargesCollected, this.onPowerChargeChange, this);

  }
}
